import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { OwnerLayout } from '../../components/layouts/OwnerLayout';
import { Plus, Edit, Trash2, Package, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const Materials = () => {
  const { api } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', unit: 'unidad', stock_quantity: 0, min_stock_alert: null, unit_cost: null });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchMaterials(); }, []);

  const fetchMaterials = async () => {
    try {
      const res = await api.get('/materials');
      setMaterials(res.data);
    } catch (error) {
      toast.error('Error al cargar materiales');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingMaterial) {
        await api.put(`/materials/${editingMaterial.id}`, formData);
        toast.success('Material actualizado');
      } else {
        await api.post('/materials', formData);
        toast.success('Material creado');
      }
      setDialogOpen(false);
      setFormData({ name: '', description: '', unit: 'unidad', stock_quantity: 0, min_stock_alert: null, unit_cost: null });
      setEditingMaterial(null);
      fetchMaterials();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al guardar material'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (material) => {
    setEditingMaterial(material);
    setFormData({ ...material });
    setDialogOpen(true);
  };

  const handleDelete = async (materialId) => {
    try {
      await api.delete(`/materials/${materialId}`);
      toast.success('Material eliminado');
      fetchMaterials();
    } catch (error) {
      toast.error('Error al eliminar material');
    }
  };

  const isLowStock = (material) => material.min_stock_alert && material.stock_quantity <= material.min_stock_alert;

  if (loading) return <OwnerLayout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div></div></OwnerLayout>;

  return (
    <OwnerLayout>
      <div className="space-y-6" data-testid="materials-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div><h1 className="font-display text-2xl font-bold text-slate-900">Materiales</h1><p className="text-slate-500">Gestiona el inventario de materiales</p></div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditingMaterial(null); setFormData({ name: '', description: '', unit: 'unidad', stock_quantity: 0, min_stock_alert: null, unit_cost: null }); } }}>
            <DialogTrigger asChild><Button data-testid="add-material-btn"><Plus className="w-4 h-4 mr-2" />Nuevo material</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editingMaterial ? 'Editar material' : 'Nuevo material'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2"><Label>Nombre *</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="material-name-input" /></div>
                <div className="space-y-2"><Label>Descripción</Label><Input value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Unidad *</Label><Input value={formData.unit} onChange={(e) => setFormData({ ...formData, unit: e.target.value })} placeholder="kg, litros, unidad..." required /></div>
                  <div className="space-y-2"><Label>Stock actual</Label><Input type="number" step="0.01" value={formData.stock_quantity} onChange={(e) => setFormData({ ...formData, stock_quantity: parseFloat(e.target.value) || 0 })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Alerta stock mínimo</Label><Input type="number" step="0.01" value={formData.min_stock_alert || ''} onChange={(e) => setFormData({ ...formData, min_stock_alert: e.target.value ? parseFloat(e.target.value) : null })} /></div>
                  <div className="space-y-2"><Label>Coste unitario (€)</Label><Input type="number" step="0.01" value={formData.unit_cost || ''} onChange={(e) => setFormData({ ...formData, unit_cost: e.target.value ? parseFloat(e.target.value) : null })} /></div>
                </div>
                <div className="flex justify-end gap-3"><Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button><Button type="submit" disabled={submitting} data-testid="save-material-btn">{submitting ? 'Guardando...' : editingMaterial ? 'Guardar' : 'Crear'}</Button></div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {materials.length === 0 ? (
          <Card><CardContent className="text-center py-12"><Package className="w-12 h-12 mx-auto mb-4 text-slate-300" /><p className="text-slate-500">Aún no tienes materiales registrados</p></CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {materials.map(material => (
              <Card key={material.id} className={isLowStock(material) ? 'border-amber-300 bg-amber-50' : ''}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg">{material.name}</CardTitle>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(material)}><Edit className="w-4 h-4" /></Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>¿Eliminar material?</AlertDialogTitle><AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(material.id)} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {material.description && <p className="text-sm text-slate-500 mb-3">{material.description}</p>}
                  <div className="flex items-center justify-between">
                    <div><p className="text-2xl font-bold">{material.stock_quantity}</p><p className="text-sm text-slate-500">{material.unit}</p></div>
                    {isLowStock(material) && <Badge className="bg-amber-100 text-amber-800 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Stock bajo</Badge>}
                  </div>
                  {material.unit_cost && <p className="text-sm text-slate-500 mt-2">Coste: €{material.unit_cost.toFixed(2)}/{material.unit}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </OwnerLayout>
  );
};

export default Materials;
